import React, { useState, useRef } from 'react';
import { PlusIcon, UploadIcon } from 'lucide-react';
import { DocumentRow } from './DocumentRow';
import { useUsageTracking } from '../../../hooks/useUsageTracking';
import { QuotaWarning } from '../../common/QuotaWarning';
import { FileTypeHelper } from '../../common/FileTypeHelper';
import { toast } from 'sonner';
import { formatBytes } from '../../../utils/formatters';
import { APP_CONFIG } from '../../../config/constants';
export const DocumentsTab = () => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    knowledgeSpaces,
    // We're not using this, but it's part of the returned object
    chatbots,
    // We're not using this, but it's part of the returned object
    checkQuota,
    refreshUsage
  } = useUsageTracking();
  // Mock quotas for documents and storage until we implement them in useUsageTracking
  const documentQuota = {
    current: 45,
    limit: 100,
    isExceeded: false,
    percentageUsed: 45
  };
  const storageQuota = {
    current: 2.5 * 1024 * 1024 * 1024,
    limit: 5 * 1024 * 1024 * 1024,
    isExceeded: false,
    percentageUsed: 50
  };
  const validateFiles = (files: FileList): {
    valid: File[];
    errors: string[];
  } => {
    const valid: File[] = [];
    const errors: string[] = [];
    const seenNames = new Set<string>();
    // Check total number of files
    if (files.length > APP_CONFIG.MAX_UPLOAD_FILES) {
      errors.push(`Maximum ${APP_CONFIG.MAX_UPLOAD_FILES} files can be uploaded at once`);
      return {
        valid,
        errors
      };
    }
    Array.from(files).forEach(file => {
      // Check file type
      if (!APP_CONFIG.SUPPORTED_FILE_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" has unsupported file type: ${file.type}`);
        return;
      }
      // Check file size
      if (file.size > APP_CONFIG.MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds maximum file size of ${formatBytes(APP_CONFIG.MAX_FILE_SIZE)}`);
        return;
      }
      // Check for duplicate names
      if (seenNames.has(file.name)) {
        errors.push(`Duplicate file name: "${file.name}"`);
        return;
      }
      seenNames.add(file.name);
      valid.push(file);
    });
    return {
      valid,
      errors
    };
  };
  const handleUpload = async (files: FileList) => {
    // Validate files first
    const {
      valid,
      errors
    } = validateFiles(files);
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset file input
      }
      return;
    }
    // Check document count quota
    if (!checkQuota('knowledgeSpaces', valid.length)) {
      toast.error('Document limit reached', {
        description: `Maximum limit of ${documentQuota.limit} documents reached. Please contact support to increase your limit.`
      });
      return;
    }
    // Calculate total upload size
    const totalSize = valid.reduce((sum, file) => sum + file.size, 0);
    // Check storage quota
    if (!checkQuota('storage', totalSize)) {
      toast.error('Storage limit reached', {
        description: `Maximum storage limit of ${formatBytes(storageQuota.limit)} reached. Please contact support to increase your limit.`
      });
      return;
    }
    try {
      setIsUploading(true);
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      await refreshUsage();
      toast.success(`Successfully uploaded ${valid.length} document${valid.length > 1 ? 's' : ''}`);
    } catch (error) {
      toast.error('Failed to upload documents');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset file input
      }
    }
  };
  // Mock document data
  const documents = [{
    id: '1',
    name: 'ADHD Research Paper.pdf',
    type: 'pdf',
    uploadedAt: new Date().toISOString(),
    size: '2.4 MB',
    summary: 'Comprehensive research on ADHD treatment methodologies and outcomes.'
  }, {
    id: '2',
    name: 'Patient Assessment Guide.docx',
    type: 'docx',
    uploadedAt: new Date().toISOString(),
    size: '1.8 MB',
    summary: 'Guidelines for conducting patient assessments and evaluations.'
  }];
  return <div className="space-y-8" data-id="element-1052">
      {/* Document Quota Warning */}
      <QuotaWarning quotaType="documents" quota={documentQuota} data-id="element-1053" />
      {/* Storage Quota Warning */}
      <QuotaWarning quotaType="storage" quota={storageQuota} data-id="element-1054" />
      <div className="flex items-start justify-between" data-id="element-1055">
        <div data-id="element-1056">
          <h2 className="text-xl font-medium text-white font-mono" data-id="element-1057">
            Documents
          </h2>
          <div className="text-sm text-gray-400 mt-1 space-y-1" data-id="element-1058">
            <p data-id="element-1059">
              {documentQuota.current} / {documentQuota.limit} Documents Used
            </p>
            <p data-id="element-1060">
              {formatBytes(storageQuota.current)} /{' '}
              {formatBytes(storageQuota.limit)} Storage Used
            </p>
          </div>
        </div>
        <label className={`
            flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-light text-white 
            rounded-none transition-colors font-mono text-sm uppercase tracking-wide cursor-pointer
            ${documentQuota.isExceeded || storageQuota.isExceeded || isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `} data-id="element-1061">
          {isUploading ? <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1062" />
              <span data-id="element-1063">Uploading...</span>
            </> : <>
              <UploadIcon size={16} data-id="element-1064" />
              <span data-id="element-1065">Upload Files</span>
            </>}
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => e.target.files && handleUpload(e.target.files)} disabled={documentQuota.isExceeded || storageQuota.isExceeded || isUploading} accept={APP_CONFIG.SUPPORTED_FILE_TYPES.join(',')} data-id="element-1066" />
        </label>
      </div>
      {/* File Type Helper */}
      <FileTypeHelper className="bg-surface p-4 border border-surface-border rounded-none" data-id="element-1067" />
      <div className="bg-surface-light border border-surface-border rounded-none overflow-hidden" data-id="element-1068">
        {documents.length > 0 ? <div className="divide-y divide-surface-border" data-id="element-1069">
            {documents.map(doc => <DocumentRow key={doc.id} document={doc} data-id="element-1070" />)}
          </div> : <div className="p-8 text-center" data-id="element-1071">
            <p className="text-gray-400" data-id="element-1072">No documents uploaded yet.</p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1073">
              Upload your first document to get started.
            </p>
          </div>}
      </div>
    </div>;
};